import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { login, verifyChallenge, selectTwoFactorDestination, startMfaEnrollAtLogin, verifyMfaEnrollAtLogin, ssoLogin } from './api';
import {
  isChallengeRequired, isDestinationPickRequired, isMfaEnrollRequired,
  type LoginRequest, type LoginResponseData, type LoginCompleted, type LoginChallengeRequired,
  type MfaEnrollAtLoginStartResponse, type ServerNavPermissions,
} from './types';
import { useAuthStore, type NavPermissions } from '../../auth/store';
import { buildNavActionMap, buildSectionGrants } from '../../layouts/navPermissions';
import { clearAllGridPrefs } from '../../components/AppDataGrid';

/** Extract division from a LoginCompleted response and set it in the store.
 *  Both divisionId and divisionCode may be null when the user has no entry
 *  in AI_UserTenantDivisionAccess yet — login still succeeds, they just
 *  won't have a division code for JobID generation until the tables are seeded. */
function applyDivision(
  setDivision: (id: string | null, code: string | null) => void,
  auth: { divisionId?: string | null; divisionCode?: string | null },
) {
  setDivision(auth.divisionId ?? null, auth.divisionCode ?? null);
}

/** Clear the grid-layout cache on sign-in, then navigate to the shell — so EVERY
 *  login starts from the factory-default grid layout. Grid customisations are
 *  session-only and reset on log-off (client decision 2026-08-05). */
function clearGridsThenNavigate(navigate: ReturnType<typeof useNavigate>): void {
  clearAllGridPrefs();
  navigate('/', { replace: true });
}

/** Translate the server permissions payload into the auth-store shape. */
function toStorePerms(p: ServerNavPermissions | undefined): NavPermissions {
  return {
    isAdmin: p?.isAdmin ?? false,
    roles:   p?.roles   ?? '',
    allowedViews: (p?.allowedNavViews ?? []).map(r => r.navigationView ?? '').filter(Boolean),
    allowedDashboards: (p?.allowedDashboards ?? [])
      .map(d => (d.dashboardId ?? '').toLowerCase())
      .filter(Boolean),
    allowedCaptions: (p?.allowedNavViews ?? []).map(r => r.navigationCaption ?? '').filter(Boolean),
    formVisibility: p?.formVisibility ?? {},
    dataReviewNav: p?.dataReviewNav,
    avaEnabled: p?.avaEnabled,
    helpCenterEnabled: p?.helpCenterEnabled,
    navActions: buildNavActionMap(p?.navActions),
    sectionGrants: buildSectionGrants(p?.allowedNavViews),
  };
}

/**
 * Login mutation (step 1 — credentials).
 *
 * <para>On a no-2FA/MFA user the server returns the JWT immediately and we
 * navigate to the shell.  On a 2FA/MFA user the server returns a challenge
 * descriptor and we DO NOT touch the store — the page swaps to the challenge
 * form and the auth state is established only after <see cref="useVerifyChallenge"/>.</para>
 *
 * Callers detect the two cases via `isChallengeRequired(data)`.
 */
export function useLogin() {
  const navigate    = useNavigate();
  const signIn      = useAuthStore((s) => s.signIn);
  const setDivision = useAuthStore((s) => s.setDivision);

  return useMutation<LoginResponseData, Error, LoginRequest>({
    mutationFn: (req) => login(req),
    onSuccess: (data, req) => {
      if (isChallengeRequired(data) || isDestinationPickRequired(data) || isMfaEnrollRequired(data)) {
        // Caller (LoginPage) reads `data` via `mutation.data` and renders the
        // challenge / destination-pick / MFA-enroll step. Don't touch the
        // session yet — MFA-enroll only completes via verifyMfaEnrollAtLogin.
        return;
      }
      const completed = data as LoginCompleted;
      signIn(completed.auth.token, completed.auth.user, req.rememberMe, toStorePerms(completed.permissions));
      applyDivision(setDivision, completed.auth);
      clearGridsThenNavigate(navigate);
    },
  });
}

/**
 * G6 SSO — handoff from the ALPACA LIMS header button (?tkn= on the URL).
 *
 * Success REPLACES any existing session (e.g. a stale "service" login left in
 * this browser) with the LIMS user the token was minted for — that's the whole
 * point of the handoff. Failure (expired/used token) drops any session too, so
 * the user lands on the login page instead of silently continuing as the old
 * user. Session is never persisted to localStorage (remember=false): the SSO
 * tab lives and dies with the LIMS session that opened it.
 */
export function useG6Sso() {
  const signIn      = useAuthStore((s) => s.signIn);
  const setDivision = useAuthStore((s) => s.setDivision);
  const logout      = useAuthStore((s) => s.logout);

  return useMutation<LoginCompleted, Error, { tkn: string }>({
    mutationFn: (req) => ssoLogin(req.tkn),
    onSuccess: (completed) => {
      signIn(completed.auth.token, completed.auth.user, false, toStorePerms(completed.permissions));
      applyDivision(setDivision, completed.auth);
      clearAllGridPrefs();
    },
    onError: () => {
      logout();
    },
  });
}

/**
 * Login mutation (step 2 — verify OTP/TOTP).
 *
 * Same destination as a no-challenge login on success: store + navigate.
 */
export function useVerifyChallenge() {
  const navigate    = useNavigate();
  const signIn      = useAuthStore((s) => s.signIn);
  const setDivision = useAuthStore((s) => s.setDivision);

  return useMutation<LoginCompleted, Error, { challengeId: string; code: string; rememberMe: boolean }>({
    mutationFn: (req) => verifyChallenge(req),
    onSuccess: (completed, req) => {
      signIn(completed.auth.token, completed.auth.user, req.rememberMe, toStorePerms(completed.permissions));
      applyDivision(setDivision, completed.auth);
      clearGridsThenNavigate(navigate);
    },
  });
}

/**
 * ALPACA parity — third leg for admin/service users under a FORCED
 * 'twofactor' mode with multiple registered addresses: picks WHICH one gets
 * the OTP (TwoFactorAuthNewViewController's destination dropdown). Always
 * resolves to a normal 2FA challenge — never signs the user in directly.
 */
export function useSelectDestination() {
  return useMutation<LoginChallengeRequired, Error, { pickId: string; index: number; rememberMe: boolean }>({
    mutationFn: (req) => selectTwoFactorDestination(req.pickId, req.index, req.rememberMe),
  });
}

/**
 * "Add another authenticator" — from an ALREADY-enrolled account's normal MFA
 * challenge screen (e.g. a shared "service" login), generates a fresh
 * secret/QR + a new "MfaEnroll" challenge. Never signs in by itself.
 */
export function useStartMfaEnrollAtLogin() {
  return useMutation<MfaEnrollAtLoginStartResponse, Error, { challengeId: string }>({
    mutationFn: (req) => startMfaEnrollAtLogin(req.challengeId),
  });
}

/**
 * MFA enroll-at-login — verify: persists the newly scanned secret AND
 * completes login in one step (first-time setup, or "add another device").
 */
export function useVerifyMfaEnrollAtLogin() {
  const navigate    = useNavigate();
  const signIn      = useAuthStore((s) => s.signIn);
  const setDivision = useAuthStore((s) => s.setDivision);

  return useMutation<LoginCompleted, Error, { challengeId: string; code: string; rememberMe: boolean; label?: string }>({
    mutationFn: (req) => verifyMfaEnrollAtLogin(req.challengeId, req.code, req.rememberMe, req.label),
    onSuccess: (completed, req) => {
      signIn(completed.auth.token, completed.auth.user, req.rememberMe, toStorePerms(completed.permissions));
      applyDivision(setDivision, completed.auth);
      clearGridsThenNavigate(navigate);
    },
  });
}

/** Helper consumer can use to discriminate at call sites. */
export { isChallengeRequired };
export type { LoginChallengeRequired };
